<template>
	<v-row>
		<v-dialog v-model="showDialog" scrollable persistent max-width="1050px" min-height="400px">
			<v-skeleton-loader v-if="loading" type="article, actions" />
			<v-card v-if="!loading">
				<v-card-title class="px-2 py-0">
					<v-col cols="12" class="d-flex justify-space-between align-center">
						<h4>
							{{ !data.id ? $t('modules.assessment.dialog.title_new') : $t('modules.assessment.dialog.title_edit') }}
						</h4>
						<div class="d-flex">
							<span v-if="data.id" class="mr-2" style="margin-top: 4px; font-size: 1rem; color: black;">{{ $t('modules.assessment-report.dialog.assessment_code') }}</span>
							<span v-if="data.id" class="mr-8" style="margin-top: 4px; font-size: 1rem; color: #C3271A;">{{ data.code }}</span>
							<v-btn icon @click="showDialog=false">
								<v-icon>mdi-close</v-icon>
							</v-btn>
						</div>
					</v-col>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pb-0" @wheel="onWheel">
					<v-container>
						<v-form ref="form">
							<v-row class="my-3 pt-2" style="border: 2px solid #E4E4E4; border-radius: 5px; background-color: #F4F4F4;">
								<v-col cols="4" class="pb-0">
									<span class="d-block black--text font-weight-bold">{{ $t('modules.assessment.dialog.candidate_firstname') }}</span>
									<v-text-field
										outlined dense
										background-color="white"
										v-model="data.candidateFirstname"
										autocomplete="none"
										:rules="[validationRules.required]"
										max="100"
									/>
								</v-col>
								<v-col cols="4" class="pb-0">
									<span class="d-block black--text font-weight-bold">{{ $t('modules.assessment.dialog.candidate_lastname') }}</span>
									<v-text-field
										outlined dense
										background-color="white"
										v-model="data.candidateLastname"
										autocomplete="none"
										:rules="[validationRules.required]"
										max="150"
									/>
								</v-col>
								<v-col cols="4" class="pb-0">
									<span class="d-block black--text font-weight-bold">{{ $t('modules.assessment.dialog.candidate_id') }}</span>
									<v-text-field
										outlined dense
										background-color="white"
										v-model="data.candidateId"
										autocomplete="none"
										:rules="[validationRules.required, validationRules.dnicif]"
										max="10"
									/>
								</v-col>
								<v-col cols="12" class="pb-1">
								<v-row>
									<v-col cols="8" class="pb-0 d-flex flex-column">
										<span class="d-block black--text font-weight-bold" style="width: 100%;">{{ $t('modules.assessment.dialog.modules') }}</span>
										<v-row class="mb-2">
											<v-col cols="12" class="pb-0 d-flex flex-row">
												<v-checkbox
													multiple
													v-model="selectedModules"
													class="mt-0 mr-4"
													:disabled="data.id?true:false"
													:value="1"
													:rules="[validationRules.requiredMultiple]"
													hide-details
												>
													<template v-slot:label>
														<span :class="'black--text'">
															{{ $t('common.assessment.modules.1') }}
														</span>
													</template>
												</v-checkbox>
												<v-checkbox
													multiple
													v-model="selectedModules"
													class="mt-0 mr-4"
													:disabled="data.id?true:false"
													:value="2"
													:rules="[validationRules.requiredMultiple]"
													hide-details
												>
													<template v-slot:label>
														<span :class="'black--text'">
															{{ $t('common.assessment.modules.2') }}
														</span>
													</template>
												</v-checkbox>
												<v-checkbox
													multiple
													v-model="selectedModules"
													class="mt-0 mr-4"
													:disabled="data.id?true:false"
													:value="3"
													:rules="[validationRules.requiredMultiple]"
													hide-details
												>
													<template v-slot:label>
														<span :class="'black--text'">
															{{ $t('common.assessment.modules.3') }}
														</span>
													</template>
												</v-checkbox>
												<v-checkbox
													multiple
													v-model="selectedModules"
													class="mt-0 mr-4"
													:disabled="data.id?true:false"
													:value="4"
													:rules="[validationRules.requiredMultiple]"
													hide-details
												>
													<template v-slot:label>
														<span :class="'black--text'">
															{{ $t('common.assessment.modules.4') }}
														</span>
													</template>
												</v-checkbox>
											</v-col>
											<v-col cols="8" class="mb-4 py-0 px-4" style="height: 12px; font-size: 12px;">
												<span class="error--text">{{ modulesErrorMessage }}</span>
											</v-col>
										</v-row>
									</v-col>
									<v-col v-if="data.id" cols="4">
										<span class="d-block black--text font-weight-bold">{{ $t('modules.assessment.dialog.status') }}</span>
										<span :style="'color: ' + getAssessmentStatusColor(data.status)">{{ getAssessmentStatusText(data.status) }}</span>
									</v-col>
								</v-row>
							</v-col>
							</v-row>
						</v-form>
						<v-row v-if="data.id" class="mt-4 d-flex black--text font-weight-bold">
							<v-col cols="6">
								<span class="d-block black--text font-weight-bold">{{ $t('modules.assessment.dialog.creation_date') }}</span>
								<span class="grey--text font-weight-regular">{{ formatLongDateTime(data.creationDate) }}</span>
							</v-col>
							<v-col cols="6" class="d-flex justify-end" align="right">
								<v-btn
									v-if="isInProgressAndHasSomeModuleProcessed"
									text
									color="#C3271A"
									class="mr-6"
									@click="showResults()">
									<v-icon color="#C3271A" class="mr-1">mdi-eye</v-icon>
									{{ $t('modules.assessment.dialog.show_report') }}
								</v-btn>
								<div>
									<span class="d-block black--text font-weight-bold">{{ $t('modules.assessment.dialog.report_date') }}</span>
									<span class="grey--text font-weight-regular">{{ reportDate }}</span>
								</div>
							</v-col>
						</v-row>
						<v-row v-if="data.id" class="mt-4 black--text font-weight-bold">
							<v-col cols="12">
								{{ $t('modules.assessment.dialog.uploaded_files') }}
							</v-col>
						</v-row>
						<v-row v-if="data.id" class="black--text font-weight-bold" style="border-bottom: 1px solid #757575;">
							<v-col cols="3">
								{{ $t('modules.assessment.dialog.module_name') }}
							</v-col>
							<v-col cols="3">
								{{ $t('modules.assessment.dialog.module_processed_date') }}
							</v-col>
							<v-col cols="3">
								{{ $t('modules.assessment.dialog.module_file_name') }}
							</v-col>
							<v-col cols="3" class="d-flex justify-end">
								{{ $t('modules.assessment.dialog.module_status') }}
							</v-col>
						</v-row>
						<div class="pb-6">
							<v-row v-for="module in data.modulesByAssessment" :key="module.id" class="mt-3" style="border-bottom: 1px solid #757575">
								<v-col cols="3">
									{{ $t(`common.assessment.modules.${module.moduleId}`) }}
								</v-col>
								<v-col cols="3">
									{{ formatLongDateTime(getModuleFileStatusDate(module)) }}
								</v-col>
								<v-col cols="3">
									{{ module.fileName ? module.fileName : '' }}
								</v-col>
								<v-col cols="3" class="d-flex justify-end">
									<span :style="'color: ' + getModuleStatusColor(module.status)">{{ getModuleStatusText(module.status) }}</span>
								</v-col>
							</v-row>
						</div>
					</v-container>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-btn v-if="data.id" color="#ff0000" @click="confirmDelete()">
						<v-icon left color="#ffffff">mdi-delete</v-icon>
						<span style="color: #ffffff;">{{ $t('common.dialog-buttons.delete') }}</span>
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn color="#C3271A" text @click="onSave()" style="font-size: 0.9em; font-weight: 400;">
						{{ $t('common.dialog-buttons.ok') }}
					</v-btn>
					<v-btn text @click="showDialog=false" style="font-size: 0.9em; font-weight: 400;">
						{{ $t('common.dialog-buttons.cancel') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<AssessmentReportDialog ref="dlgAssessmentReport" :data="data.modulesByAssessment" />
		<ConfirmDialog ref="dlgConfirm"
			:title="$t('common.confirm-dialog.title')"
			:message="$t('common.confirm-dialog.text')"
			:titleOk="$t('common.dialog-buttons.delete')"
			:titleCancel="$t('common.dialog-buttons.cancel')"
			@confirmed="deleteItem()" />
	</v-row>
</template>

<script>
import BaseModule from '../common/BaseModule.js';
import AssessmentReportDialog from './AssessmentReportDialog.vue';
import ConfirmDialog from '../common/ConfirmDialog.vue';
import api from '../../api/humanway.js';

export default {
	name: 'AssessmentDialog',
	mixins: [BaseModule],
	components: {
		AssessmentReportDialog, ConfirmDialog
	},
	data() {
		return {
			showDialog: false,
			loading: false,
			data: {},
			modules: [
				{ id: 1, text: this.$t('common.assessment.modules.1') },
				{ id: 2, text: this.$t('common.assessment.modules.2') }
			],
			selectedModules: [],
			modulesErrorMessage: '',
		}
	},
	computed: {
		isInProgressAndHasSomeModuleProcessed() {
			return this.data.status >= this.constants.AS_IN_PROGRESS_STATUS &&
				this.data.modulesByAssessment.some(m => m.status === this.constants.MO_PROCESSED_STATUS);
		},
		reportDate() {
			if (!this.data) {
				return '---';
			}
			if (this.data.finishedDate) {
				return this.formatLongDateTime(this.data.finishedDate);
			}
			if (this.data.modulesByAssessment.length === 0) {
				return '---';
			}
			if (!this.data.modulesByAssessment.some(m => m.processedDate)) {
				return '---';
			}
			const modules = this.data.modulesByAssessment.filter(m => m.processedDate).sort(
				function(a, b) {
					return new Date(b.processedDate).getTime() - new Date(a.processedDate).getTime();
				}
			);
			return this.formatLongDateTime(modules[0].processedDate);
		}
	},
	methods: {
		onWheel(event) {
			event.stopPropagation();
		},
		open(action, payload) {
			this.selectedModules = [];
			this.modulesErrorMessage = '';

			this.showDialog = true;

			if (this.$refs.form) {
				this.$refs.form.resetValidation();
			}

			if (!payload) {
				this.data = {};
				return;
			}

			this.loading = true;

			api.getAssessment(payload,
				res => {
					this.data = res;
					this.loading = false;

					this.selectedModules = this.data.modules.split(',').map(Number);
				},
				error => {
					this.$log.error(error);
					this.$emit('show-alert', { text: error, color: 'error' });
					this.loading = false;
				}
			);
		},
		showResults() {
			this.$refs.dlgAssessmentReport.open(this.data);
		},
		getModuleStatusColor(status) {
			let color = '';

			switch (status) {
				case this.constants.MO_PENDING_STATUS:
					color = '#21A6E7';
					break;
				case this.constants.MO_FINISHED_STATUS:
					color = '#FF9800';
					break;
				case this.constants.MO_PROCESSED_STATUS:
					color = '#3BA70C';
					break;
			}

			return color;
		},
		getModuleStatusText(status) {
			let text = '';

			switch (status) {
				case this.constants.MO_PENDING_STATUS:
					text = this.$t('common.assessment.modules.status.pending');
					break;
				case this.constants.MO_FINISHED_STATUS:
					text = this.$t('common.assessment.modules.status.finished');
					break;
				case this.constants.MO_PROCESSED_STATUS:
					text = this.$t('common.assessment.modules.status.processed');
					break;
			}

			return text.toUpperCase();
		},
		getModuleFileStatusDate(module) {
			if (module.status === this.constants.MO_PENDING_STATUS) {
				return null;
			}
			if (module.status === this.constants.MO_FINISHED_STATUS) {
				return module.finishedDate;
			}
			if (module.status === this.constants.MO_PROCESSED_STATUS) {
				return module.processedDate;
			}
		},
		onSave() {
			if (!this.$refs.form.validate()) {
				if (this.selectedModules.length === 0) {
					this.modulesErrorMessage = this.$t('common.validations.select_at_least_one');
				}
				return;
			}

			this.modulesErrorMessage = '';

			this.data.modules = this.selectedModules.sort().toString();

			this.loading = true;

			api.storeAssessment(this.data,
				() => {
					this.loading = false;
					this.showDialog = false;
					this.$emit('refresh');
				},
				error => {
					this.$log.error(error);
					this.loading = false;
					this.$emit('show-alert', { text: error, color: 'error' });
				}
			);
		},
		confirmDelete() {
			this.$refs.dlgConfirm.confirm(this.data);
		},
		deleteItem() {
			this.loading = true;

			api.deleteAssessment(this.data.id,
				() => {
					this.showDialog = false;
					this.loading = false;
					this.$emit('refresh');
				},
				error => {
					this.$log.error(error);
					this.loading = false;
					this.$emit('show-alert', { text: error, color: 'error' });
				}
			);
		}
	},
}
</script>